/**
 * How far a budget of stamina actually goes.
 *
 * Used three ways: by the generator, to hold itself to the guarantee that no
 * single shore reaches every relic in a day (`docs/rules.md` section 2); by
 * `scripts/tune.ts`, to report what a shore is worth over thousands of seeds;
 * and by the server, to answer "could this player have got there" without
 * replaying their moves.
 *
 * Stamina is paid on the tile entered, so the starting tile costs nothing.
 * Costs are 1, 2 or 3, which is small enough that a bucket queue beats a heap
 * and short enough to keep the whole thing obvious.
 */

import { MOVEMENT_COST, type Terrain } from './game-config.ts'
import { index, inBounds, neighbours, position, type Position } from './tiles.ts'

/** Cheapest cost to each tile that can be entered, keyed by row-major index. */
export type ReachMap = ReadonlyMap<number, number>

/**
 * Every tile reachable from `from` for at most `stamina`, with what it costs to
 * get there. `null` stamina is the replay budget: unlimited (Q1.2).
 *
 * The origin is included at cost 0. An origin in the water reaches nothing,
 * because nobody can be standing there.
 */
export function reachableWithin(
  tiles: readonly Terrain[],
  from: Position,
  stamina: number | null,
): ReachMap {
  const reached = new Map<number, number>()
  if (!inBounds(from)) return reached
  const origin = index(from)
  const originTerrain = tiles[origin]
  if (originTerrain === undefined || MOVEMENT_COST[originTerrain] === null) return reached

  const budget = stamina ?? Number.POSITIVE_INFINITY
  if (budget < 0) return reached
  reached.set(origin, 0)

  // Dial's algorithm: one bucket per cost, walked in order. The largest single
  // step is 3, so a tile is never inserted more than three buckets ahead.
  const buckets: number[][] = []
  const push = (tile: number, cost: number) => {
    ;(buckets[cost] ??= []).push(tile)
  }
  push(origin, 0)

  for (let cost = 0; cost < buckets.length; cost += 1) {
    const bucket = buckets[cost]
    if (bucket === undefined) continue
    for (const tile of bucket) {
      // A tile can sit in several buckets; only its cheapest entry counts.
      if (reached.get(tile) !== cost) continue
      for (const neighbour of neighbours(position(tile))) {
        const terrain = tiles[index(neighbour)]
        if (terrain === undefined) continue
        const step = MOVEMENT_COST[terrain]
        if (step === null) continue
        const total = cost + step
        if (total > budget) continue
        const known = reached.get(index(neighbour))
        if (known !== undefined && known <= total) continue
        reached.set(index(neighbour), total)
        push(index(neighbour), total)
      }
    }
  }

  return reached
}

/** Every tile that can be walked to at all, ignoring stamina. */
export function connectedLand(tiles: readonly Terrain[], from: Position): ReachMap {
  return reachableWithin(tiles, from, null)
}
